import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';

export const BEE_STATUS_LABELS = Object.freeze({
  PENDING: 'Aguardando entregador',
  PICKUP_ONGOING: 'Entregador a caminho da loja',
  ARRIVED_AT_MERCHANT: 'Entregador chegou à loja',
  ORDER_PICKED: 'Pedido coletado',
  DELIVERY_ONGOING: 'Entrega em andamento',
  ARRIVED_AT_CUSTOMER: 'Entregador chegou ao cliente',
  DELIVERY_FINISHED: 'Entrega concluída',
  RETURNING_TO_MERCHANT: 'Retornando à loja',
  CANCELLED: 'Entrega cancelada',
});

const NEXT_SANDBOX_EVENTS = Object.freeze({
  PENDING: ['PICKUP_ONGOING', 'CANCELLED'],
  PICKUP_ONGOING: ['ARRIVED_AT_MERCHANT', 'CANCELLED'],
  ARRIVED_AT_MERCHANT: ['ORDER_PICKED', 'CANCELLED'],
  ORDER_PICKED: ['DELIVERY_ONGOING', 'RETURNING_TO_MERCHANT', 'CANCELLED'],
  DELIVERY_ONGOING: ['ARRIVED_AT_CUSTOMER', 'RETURNING_TO_MERCHANT', 'CANCELLED'],
  ARRIVED_AT_CUSTOMER: ['DELIVERY_FINISHED', 'RETURNING_TO_MERCHANT', 'CANCELLED'],
  RETURNING_TO_MERCHANT: ['CANCELLED'],
});

const BeeDeliveryStatusPanel = ({ status, loading = false, error = '', advancing = false, canManageSandbox = false,
  canManageConfirmation = false, onRefresh, onAdvance, onConfirmCode }) => {
  const [selectedEvent, setSelectedEvent] = useState('');
  const [localCode,setLocalCode] = useState('');
  const currentEvent = String(status?.status || '').trim().toUpperCase();
  const isIfood=status?.provider==='ifood';
  const completed=isIfood ? currentEvent==='DELIVERED' : currentEvent==='DELIVERY_FINISHED';
  const nextEvents = NEXT_SANDBOX_EVENTS[currentEvent] || [];
  const canAdvance = canManageSandbox && !status?.stale && status?.sandbox === true
    && status?.capabilities?.canAdvanceBeeSandboxStatus === true
    && status?.reservationStatus !== 'SUBMISSION_UNKNOWN'
    && !status?.terminal
    && nextEvents.length > 0;

  useEffect(() => { setSelectedEvent(''); }, [currentEvent, status?.deliveryId]);
  useEffect(() => { setLocalCode(''); }, [status?.deliveryId,status?.localConfirmationStatus]);
  const canConfirm = canManageConfirmation && !status?.stale && !status?.terminalReviewRequired && typeof onConfirmCode === 'function' &&
    status?.capabilities?.canConfirmLocalCode === true && completed &&
    status?.localConfirmationStatus === 'PENDING';

  if (!status?.available && !error && !loading) return null;

  return (
    <Box mt={2} data-testid="bee-delivery-status">
      <Typography variant="h6" gutterBottom>{isIfood?'Entrega iFood':'Entrega Bee Delivery'}</Typography>
      <Stack spacing={1} alignItems="flex-start">
        {status?.available && (
          <>
            <Chip
              label={status.reservationStatus === 'SUBMISSION_UNKNOWN'
                ? 'Solicitação em verificação'
                : status.statusLabel || BEE_STATUS_LABELS[currentEvent] || 'Status em verificação'}
              color={status.reservationStatus === 'SUBMISSION_UNKNOWN'
                ? 'warning'
                : currentEvent === 'CANCELLED' ? 'error' : status.terminal ? 'success' : 'info'}
              size="small"
            />
            {status.deliveryId && <Typography variant="body2">Identificador {isIfood?'iFood':'Bee'}: {status.deliveryId}</Typography>}
            {status.reservationStatus === 'SUBMISSION_UNKNOWN' && (
              <Alert severity="warning">O prestador ainda está confirmando a solicitação. Atualize o status; não faça outra contratação para este pedido.</Alert>
            )}
          </>
        )}
        {loading && <Typography variant="body2">Consultando status da entrega...</Typography>}
        {status?.stale && <Alert severity="warning">O status do prestador está indisponível. A reserva permanece protegida; consulte o journal para conferir a operação.</Alert>}
        {status?.terminalReviewRequired && <Alert severity="warning">Há conflito no encerramento informado pelo prestador. O código local está bloqueado até a conferência auditada no journal.</Alert>}
        {error && <Alert severity="warning">{error}</Alert>}
        <Button variant="outlined" onClick={onRefresh} disabled={loading || advancing}>{isIfood?'Atualizar status iFood':'Atualizar status Bee'}</Button>
        {status?.localConfirmationStatus === 'PENDING' && completed && !status?.stale && !status?.terminalReviewRequired && (
          <Alert severity="info">O prestador informou a entrega concluída. A confirmação local ainda depende do código informado pelo cliente.</Alert>
        )}
        {status?.localConfirmationStatus === 'CONFIRMED' && <Typography variant="body2">Código local conferido.</Typography>}
        {canConfirm && (
          <Box sx={{display:'grid',gap:1,width:'100%'}}>
            <TextField label="Código do cliente" value={localCode} type="password"
              inputProps={{inputMode:'numeric',maxLength:4,autoComplete:'off'}}
              onChange={event => setLocalCode(event.target.value.replace(/\D/g,'').slice(0,4))}
              disabled={loading || advancing} />
            <Button variant="contained" disabled={!/^\d{4}$/.test(localCode) || loading || advancing}
              onClick={() => { const code = localCode; setLocalCode(''); onConfirmCode(code); }}>
              Confirmar código local
            </Button>
          </Box>
        )}
        {canAdvance && (
          <Box sx={{ width: '100%', display: 'grid', gap: 1 }}>
            <TextField
              select
              label="Próximo status de teste"
              value={selectedEvent}
              onChange={(event) => setSelectedEvent(event.target.value)}
              disabled={advancing || loading}
              size="small"
              fullWidth
            >
              {nextEvents.map((event) => <MenuItem key={event} value={event}>{BEE_STATUS_LABELS[event]}</MenuItem>)}
            </TextField>
            <Button variant="outlined" onClick={() => onAdvance(selectedEvent)} disabled={!selectedEvent || advancing || loading}>
              {advancing ? 'Simulando...' : 'Avançar status no sandbox'}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default BeeDeliveryStatusPanel;
